// components/AsthmaCalculator.tsx
import React, { CSSProperties, useState } from 'react';
import { cancelButton, confirmButton } from '../../../components/Buttons';
import './AsthmaCalculator.scss';

interface AsthmaCalculatorProps {
  // Assessment params (Inputs)
  age: string;
  setAge: (value: string) => void;
  respiratoryRate: string;
  setRespiratoryRate: (value: string) => void;
  oxygenSaturation: string;
  setOxygenSaturation: (value: string) => void;

  oxygenSupport: boolean;
  setOxygenSupport: (value: boolean) => void;

  auscultation: string;
  setAuscultation: (value: string) => void;
  retractions: string;
  setRetractions: (value: string) => void;
  dyspnea: string;
  setDyspnea: (value: string) => void;

  // Actions
  onContinue: () => void;
  onReset: () => void;
  onBack?: () => void; // Optional - exists only in recheck

  hideAge?: boolean;
}

const AsthmaCalculator: React.FC<AsthmaCalculatorProps> = ({
  age, setAge,
  respiratoryRate, setRespiratoryRate,
  oxygenSaturation, setOxygenSaturation,
  oxygenSupport, setOxygenSupport,
  auscultation, setAuscultation,
  retractions, setRetractions,
  dyspnea, setDyspnea,
  onContinue,
  onReset,
  onBack,
  hideAge = false,
}) => {

  // 1. New state to manage error display
  const [showErrors, setShowErrors] = useState(false);

  // Helper function for validation
  const validate = () => {
    // If hiding age, it is not considered missing (or we assume it already exists)
    const isAgeMissing = !hideAge && !age;

    if (
      isAgeMissing ||
      !respiratoryRate ||
      !oxygenSaturation ||
      !auscultation ||
      !retractions ||
      !dyspnea
    ) {
      return false;
    }
    return true;
  };

  // 2. New logic for continue button
  const handleContinueClick = () => {
    if (validate()) {
      onContinue();
    } else {
      setShowErrors(true);
      // Optional: scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // 3. Small component for error message (to prevent code duplication)
  const ErrorMsg = ({ msg = "שדה חובה" }: { msg?: string }) => (
    <div className="validation-message">
      <span className="error-icon-badge">!</span>
      <span>{msg}</span>
    </div>
  );

  return (
    <div className="asthma-wrapper">
      <div className="asthma-calculator-inner-container" dir="rtl">

        <span className="asthma-subtitle">*יש למלא את כל הערכים</span>

        <div className="asthma-calculator-card">

          <div className="asthma-calculator-inputs-column" dir="rtl">

            {/* Age */}
            {!hideAge && (
              <>
                <div className="form-row">
                  <label htmlFor="age" className="form-label">גיל הילד</label>
                  <select
                    id="age"
                    className={`form-select ${showErrors && !age ? 'input-error' : ''}`}
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                  >
                    <option value="" disabled hidden>בחירת טווח</option>
                    <option value="1-3">1-3 שנים</option>
                    <option value="4-5">4-5 שנים</option>
                    <option value="6-12">6-12 שנים</option>
                    <option value=">12">מעל 12 שנים</option>
                  </select>
                </div>
                {/* Error message must be outside form-row because form-row is display: contents */}
                {showErrors && !age && <ErrorMsg msg="לא נבחרה אף אפשרות" />}
              </>
            )}

            {/* Respiratory Rate */}
            <div className="form-row">
              <label htmlFor="respiratoryRate" className="form-label">
                קצב נשימה <br /> (RR)
              </label>
              <input
                type="number"
                id="respiratoryRate"
                className={`form-input ${showErrors && !respiratoryRate ? 'input-error' : ''}`}
                dir="ltr"
                value={respiratoryRate}
                onChange={(e) => setRespiratoryRate(e.target.value)}
                placeholder="לדקה"
              />
            </div>
            {showErrors && !respiratoryRate && <ErrorMsg />}

            {/* Oxygen Saturation */}
            <div className="form-row">
              <label htmlFor="oxygenSaturation" className="form-label">
                רווי חמצן <br /> (%)
              </label>
              <input
                type="number"
                id="oxygenSaturation"
                className={`form-input ${showErrors && !oxygenSaturation ? 'input-error' : ''}`}
                dir="ltr"
                value={oxygenSaturation}
                onChange={(e) => setOxygenSaturation(e.target.value)}
                placeholder="אחוזים"
                min="70"
                max="100"
              />
            </div>
            {showErrors && !oxygenSaturation && <ErrorMsg />}

            {/* Oxygen support - no validation because it is boolean (yes/no) */}
            <div className="form-row">
              <label htmlFor="oxygenSupport" className="form-label">
                תוספת <br /> חמצן
              </label>
              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  id="oxygenSupport"
                  checked={oxygenSupport}
                  onChange={(e) => setOxygenSupport(e.target.checked)}
                  className="form-checkbox"
                />
              </div>
            </div>
            {/* Boolean does not need error message because it is always valid (true/false) */}

            {/* Auscultation */}
            <div className="form-row">
              <label htmlFor="auscultation" className="form-label">האזנה</label>
              <select
                id="auscultation"
                className={`form-select ${showErrors && !auscultation ? 'input-error' : ''}`}
                value={auscultation}
                onChange={(e) => setAuscultation(e.target.value)}
              >
                <option value="" disabled hidden>בחירת טווח</option>
                <option value="mild">בועית או צפצופים סוף אקספיריום</option>
                <option value="moderate">צפצופים אקספיריום מפושטים</option>
                <option value="severe">צפצופים אינספ + אקספ, כניסת אויר מופחתת</option>
              </select>
            </div>
            {showErrors && !auscultation && <ErrorMsg msg="לא נבחרה אף אפשרות" />}

            {/* Retractions */}
            <div className="form-row">
              <label htmlFor="retractions" className="form-label">רתיעות</label>
              <select
                id="retractions"
                className={`form-select ${showErrors && !retractions ? 'input-error' : ''}`}
                value={retractions}
                onChange={(e) => setRetractions(e.target.value)}
              >
                <option value="" disabled hidden>בחירת טווח</option>
                <option value="none">ללא או בין צלעיות</option>
                <option value="moderate">בין ותת צלעיות</option>
                <option value="severe">בין ותת צלעיות + מעל עצמות הבריח</option>
              </select>
            </div>
            {showErrors && !retractions && <ErrorMsg msg="לא נבחרה אף אפשרות" />}

            {/* Dyspnea */}
            <div className="form-row">
              <label htmlFor="dyspnea" className="form-label">
                דיספנאה / <br /> יכולת דיבור
              </label>
              <select
                id="dyspnea"
                className={`form-select ${showErrors && !dyspnea ? 'input-error' : ''}`}
                value={dyspnea}
                onChange={(e) => setDyspnea(e.target.value)}
              >
                <option value="" disabled hidden>בחירת טווח</option>
                <option value="mild">אוכל/משחק ללא קושי, דיבור במשפטים</option>
                <option value="moderate">אוכל בהפסקות, דיבור במשפטים חלקיים</option>
                <option value="severe">לא יכול לאכול/לשחק, דיבור במילים בודדות</option>
              </select>
            </div>
            {showErrors && !dyspnea && <ErrorMsg msg="לא נבחרה אף אפשרות" />}
          </div>

          <div className="asthma-buttons-row">
            <button
              type="button"
              style={confirmButton as CSSProperties}
              // Removed disabled={isContinueDisabled}
              onClick={handleContinueClick}
            >
              {hideAge ? 'סיום הערכה' : 'המשך'}
            </button>

            {/* Back button - displayed only if onBack function was passed */}
            {onBack && (
              <button
                type="button"
                style={cancelButton as CSSProperties}
                onClick={onBack}
              >
                חזור
              </button>
            )}

            <button
              type="button"
              style={cancelButton as CSSProperties}
              onClick={() => {
                setShowErrors(false); // Reset errors on clear
                onReset();
              }}
            >
              נקה
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AsthmaCalculator;